/**
 * useAllocationCalculations - Allocation calculations hook for Roster feature
 * 
 * This hook manages all allocation calculations with caching and optimization.
 * It uses the AllocationProcessor service to handle complex calculations.
 * 
 * Key responsibilities:
 * - Provide memoized allocation calculations
 * - Handle cache invalidation when data changes
 * - Optimize performance for large datasets
 * - Provide allocation utilities and statistics
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllocationProcessor } from '../services/AllocationProcessor';

// Create singleton processor instance
const allocationProcessor = new AllocationProcessor();

export function useAllocationCalculations(roster, outcomeAllocations, year) {
  // State for tracking calculation status
  const [calculationStats, setCalculationStats] = useState({
    lastCalculationTime: null,
    totalCalculations: 0,
    cacheHitRate: 0
  });
  
  // Refs for tracking data changes
  const lastRosterHash = useRef('');
  const lastAllocationsHash = useRef('');
  const lastYear = useRef(year);
  
  /**
   * Create hash for data change detection
   */
  const createDataHash = useCallback((roster, outcomeAllocations) => {
    const rosterHash = roster.map(m => m.id).sort().join(',');
    const allocationsHash = outcomeAllocations.map(o => `${o.outcome_id}-${o.staffed_roles?.length || 0}`).join(',');
    return `${rosterHash}|${allocationsHash}`;
  }, []);
  
  /**
   * Clear cache when data changes significantly
   */
  useEffect(() => {
    const currentHash = createDataHash(roster, outcomeAllocations);
    const currentYear = year;
    
    // Check if we need to clear cache
    let shouldClearCache = false;
    
    if (currentHash !== lastRosterHash.current) {
      console.log('useAllocationCalculations: Roster or allocations data changed, clearing cache');
      shouldClearCache = true;
      lastRosterHash.current = currentHash;
    }
    
    if (currentYear !== lastYear.current) {
      console.log('useAllocationCalculations: Year changed, clearing cache');
      shouldClearCache = true;
      lastYear.current = currentYear;
    }
    
    if (shouldClearCache) {
      allocationProcessor.clearCache();
    }
  }, [roster, outcomeAllocations, year, createDataHash]);
  
  /**
   * Get member outcome allocations with caching
   */
  const getMemberOutcomeAllocations = useCallback((memberId) => {
    const startTime = performance.now();
    
    try {
      const result = allocationProcessor.getMemberOutcomeAllocations(memberId, outcomeAllocations, roster);
      
      // Update stats
      const endTime = performance.now();
      const stats = allocationProcessor.getCacheStats();
      
      setCalculationStats({
        lastCalculationTime: endTime - startTime,
        totalCalculations: stats.calculations,
        cacheHitRate: parseFloat(stats.cacheEfficiency.replace('%', ''))
      });
      
      return result;
    } catch (error) {
      console.error('useAllocationCalculations: Error getting member allocations:', error);
      return {};
    }
  }, [roster, outcomeAllocations]);
  
  /**
   * Calculate monthly allocations from date ranges
   */
  const calculateMonthlyAllocationsFromDateRanges = useCallback((dateRanges, projectStartMonth = null) => {
    try {
      return allocationProcessor.calculateMonthlyAllocationsFromDateRanges(dateRanges, projectStartMonth);
    } catch (error) {
      console.error('useAllocationCalculations: Error calculating monthly allocations:', error);
      return Array(12).fill(0);
    }
  }, []);
  
  /**
   * Memoized outcome allocations for all roster members
   */
  const memoizedOutcomeAllocations = useMemo(() => {
    console.log('📊 useAllocationCalculations: Recalculating memoized outcome allocations...');
    console.log('📊 useAllocationCalculations: Current roster length:', roster.length);
    console.log('📊 useAllocationCalculations: Current outcome allocations length:', outcomeAllocations.length);
    
    const memberOutcomes = new Map();
    
    if (roster.length === 0 || outcomeAllocations.length === 0) {
      console.log('⚠️ useAllocationCalculations: No roster or allocation data available');
      return memberOutcomes;
    }
    
    // Batch process all members for better performance
    const memberIds = roster.map(member => member.id);
    const batchResults = allocationProcessor.batchProcessMembers(memberIds, outcomeAllocations, roster);
    
    // Convert batch results to Map
    batchResults.forEach((allocations, memberId) => {
      if (Object.keys(allocations).length > 0) {
        memberOutcomes.set(memberId, allocations);
      }
    });
    
    console.log('📊 useAllocationCalculations: Total members with allocations:', memberOutcomes.size);
    
    // Debug: Log which members have allocations
    if (process.env.NODE_ENV === 'development' && memberOutcomes.size > 0) {
      console.log('📊 useAllocationCalculations: Members with allocations:');
      memberOutcomes.forEach((outcomes, memberId) => {
        const member = roster.find(m => m.id === memberId);
        console.log(`  - ${member?.name || 'Unknown'} (${memberId}): ${Object.keys(outcomes).length} outcomes`);
      });
    } else if (memberOutcomes.size === 0) {
      console.warn('⚠️ useAllocationCalculations: No members have allocation data!');
      console.log('📊 useAllocationCalculations: Debug info:');
      console.log('  - Roster length:', roster.length);
      console.log('  - Outcome allocations length:', outcomeAllocations.length);
      console.log('  - Sample roster member:', roster[0]);
      if (outcomeAllocations.length > 0) {
        console.log('  - Sample outcome allocation:', outcomeAllocations[0]);
      }
    }
    
    return memberOutcomes;
  }, [roster, outcomeAllocations]);
  
  /**
   * Calculate member summary statistics
   */
  const calculateMemberSummary = useCallback(() => {
    try {
      return allocationProcessor.calculateMemberSummary(roster, outcomeAllocations);
    } catch (error) {
      console.error('useAllocationCalculations: Error calculating member summary:', error);
      return {
        totalMembers: roster.length,
        totalAssigned: 0,
        totalUnassigned: roster.length,
        overAllocated: 0,
        fullyAllocated: 0,
        underAllocated: 0,
        hasAllocationData: false
      };
    }
  }, [roster, outcomeAllocations]);
  
  /**
   * Process outcome allocations data
   */
  const processOutcomeAllocations = useCallback((allocationsData) => {
    try {
      return allocationProcessor.processOutcomeAllocations(allocationsData);
    } catch (error) {
      console.error('useAllocationCalculations: Error processing outcome allocations:', error);
      return [];
    }
  }, []);
  
  /**
   * Force recalculation for specific member
   */
  const forceRecalculateForMember = useCallback((memberId) => {
    console.log('🔄 useAllocationCalculations: Force recalculating for member:', memberId);
    allocationProcessor.forceRecalculateForMember(memberId);
  }, []);
  
  /**
   * Clear all calculation cache
   */
  const clearCalculationCache = useCallback(() => {
    console.log('🧹 useAllocationCalculations: Clearing all calculation cache');
    allocationProcessor.clearCache();
    setCalculationStats({
      lastCalculationTime: null,
      totalCalculations: 0,
      cacheHitRate: 0
    });
  }, []);
  
  /**
   * Get calculation performance statistics
   */
  const getCalculationStats = useCallback(() => {
    const processorStats = allocationProcessor.getCacheStats();
    return {
      ...calculationStats,
      processorStats,
      dataHashes: {
        lastRosterHash: lastRosterHash.current,
        lastAllocationsHash: lastAllocationsHash.current,
        lastYear: lastYear.current
      }
    };
  }, [calculationStats]);
  
  /**
   * Validate allocation data integrity
   */
  const validateAllocationData = useCallback(() => {
    const validation = {
      isValid: true,
      errors: [],
      warnings: [],
      stats: {
        totalOutcomes: outcomeAllocations.length,
        totalRoles: 0,
        membersWithAllocations: 0,
        orphanedRoles: 0
      }
    };
    
    try {
      const memberIds = new Set(roster.map(m => m.id));
      const allocatedMemberIds = new Set();
      
      outcomeAllocations.forEach((outcome, outcomeIndex) => {
        if (!outcome.outcome_id && !outcome.id) {
          validation.errors.push(`Outcome at index ${outcomeIndex} missing ID`);
          validation.isValid = false;
        }
        
        if (!outcome.outcome_name && !outcome.name) {
          validation.warnings.push(`Outcome at index ${outcomeIndex} missing name`);
        }
        
        if (outcome.staffed_roles && Array.isArray(outcome.staffed_roles)) {
          validation.stats.totalRoles += outcome.staffed_roles.length;
          
          outcome.staffed_roles.forEach((role, roleIndex) => {
            if (!role.member_id) {
              validation.errors.push(`Role at outcome ${outcomeIndex}, role ${roleIndex} missing member_id`);
              validation.isValid = false;
            } else {
              if (memberIds.has(role.member_id)) {
                allocatedMemberIds.add(role.member_id);
              } else {
                validation.stats.orphanedRoles++;
                validation.warnings.push(`Role references non-existent member: ${role.member_id}`);
              }
            }
            
            if (!role.role_name && !role.name) {
              validation.warnings.push(`Role at outcome ${outcomeIndex}, role ${roleIndex} missing name`);
            }
          });
        }
      });
      
      validation.stats.membersWithAllocations = allocatedMemberIds.size;
      
      if (validation.stats.orphanedRoles > 0) {
        validation.warnings.push(`Found ${validation.stats.orphanedRoles} roles referencing non-existent members`);
      }
      
    } catch (error) {
      validation.errors.push(`Validation error: ${error.message}`);
      validation.isValid = false;
    }
    
    return validation;
  }, [roster, outcomeAllocations]);
  
  /**
   * Get allocation insights and recommendations
   */
  const getAllocationInsights = useCallback(() => {
    const insights = {
      recommendations: [],
      warnings: [],
      opportunities: [],
      statistics: {
        utilizationRate: 0,
        overAllocationRate: 0,
        unassignedRate: 0
      }
    };
    
    try {
      const summary = calculateMemberSummary();
      
      if (summary.totalMembers > 0) {
        insights.statistics.utilizationRate = (summary.totalAssigned / summary.totalMembers) * 100;
        insights.statistics.overAllocationRate = (summary.overAllocated / summary.totalMembers) * 100;
        insights.statistics.unassignedRate = (summary.totalUnassigned / summary.totalMembers) * 100;
        
        // Generate recommendations
        if (insights.statistics.unassignedRate > 30) {
          insights.recommendations.push('High number of unassigned members - consider reviewing project staffing needs');
        }
        
        if (insights.statistics.overAllocationRate > 10) {
          insights.warnings.push('Multiple members are over-allocated - review workload distribution');
        }
        
        if (insights.statistics.utilizationRate < 70) {
          insights.opportunities.push('Low utilization rate - potential for additional project assignments');
        }
        
        if (summary.totalAssigned > 0 && summary.overAllocated === 0) {
          insights.opportunities.push('Good allocation balance - no over-allocated members detected');
        }
      }
      
    } catch (error) {
      console.error('useAllocationCalculations: Error generating insights:', error);
      insights.warnings.push('Unable to generate allocation insights due to data issues');
    }
    
    return insights;
  }, [calculateMemberSummary]);
  
  return {
    // Core calculation functions
    getMemberOutcomeAllocations,
    calculateMonthlyAllocationsFromDateRanges,
    memoizedOutcomeAllocations,
    
    // Data processing
    processOutcomeAllocations,
    calculateMemberSummary,
    
    // Cache management
    forceRecalculateForMember,
    clearCalculationCache,
    
    // Statistics and insights
    getCalculationStats,
    validateAllocationData,
    getAllocationInsights,
    
    // Performance metrics
    calculationStats
  };
}